import React, { useState, useEffect } from "react";
import Carousel from "react-bootstrap/Carousel";
import AOS from "aos";

// Material UI Components
import {
  Box,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

// Material UI Icons
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowRightRoundedIcon from "@mui/icons-material/ArrowRightRounded";

export default function EventCarousel(props) {
  const { slides } = props;
  const [carouselIndex, setCarouselIndex] = useState(0);

  const handleSelect = (selectedIndex) => {
    setCarouselIndex(selectedIndex);
  };

  // Function to handle PDF download
  const downloadPdf = (pdf) => {
    const link = document.createElement("a");
    link.href = pdf.path;
    link.download = pdf.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    AOS.init({
      duration: 700,
      easing: "ease-in-out",
      once: true,
    });
  }, []);

  return (
    <Box data-aos="fade-up">
      <Carousel
        activeIndex={carouselIndex}
        onSelect={handleSelect}
        interval={null}
        controls={true}
        touch={false}
        style={{ width: "100%" }}
      >
        {slides.map((slide, index) => (
          <Carousel.Item key={index} style={{ marginBottom: "100px" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: {
                  xs: "center",
                  sm: "center",
                  md: "flex-start",
                  lg: "flex-start",
                  xl: "flex-start",
                },
                flexDirection: {
                  xs: "column",
                  sm: "column",
                  md: "row",
                  lg: "row",
                  xl: "row",
                },
                px: 2,
              }}
            >
              <img
                className={`carousel-image-${slide.category}-${index}`}
                src={slide.image}
                alt={`Slide ${index + 1}`}
                style={{
                  margin: "10px",
                  width: "100%",
                  height: "auto", // This ensures aspect ratio is maintained
                  maxWidth: "400px", // Restricts the max width to 400px
                  maxHeight: "400px", // Optionally restrict the max height to 400px if needed
                  objectFit: "contain", // Ensures the image fits within the container
                  transition: "transform 0.3s ease-in-out", // Smooth rotation
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "rotate(2deg)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "rotate(0deg)";
                }}
              />
              <Box sx={{ color: "white", maxWidth: "600px", px: 4, my: 1 }}>
                <Box>
                  <Typography
                    onClick={() => window.open(slide.youtube_link, "_blank")}
                    variant="h5"
                    sx={{
                      color: "black",
                      fontWeight: "bold",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "fit-content",
                      backgroundColor: "white",
                      px: 2,
                      py: 1,
                      borderRadius: "16px",
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline",
                        "& .icon": {
                          display: "inline-block", // Show the icon on hover
                          transform: "rotate(360deg)", // Rotate the icon
                        },
                      },
                    }}
                  >
                    {slide.category}
                    <ArrowOutwardRoundedIcon
                      className="icon"
                      fontSize="large"
                      sx={{
                        ml: 2,
                        transition: "transform 0.5s ease-in-out", // Smooth rotation
                      }}
                    />
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: "rgba(255,255,255,0.1)",
                    borderRadius: "16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    px: 2,
                    my: 1,
                    fontWeight: "bold",
                  }}
                >
                  <Typography fontWeight="bold" sx={{ my: 1 }}>
                    {slide.title}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {slide.document.path !== "" ? (
                      <IconButton
                        sx={{
                          mr: 1,
                          width: 40, // Equal width
                          height: 40, // Equal height
                          borderRadius: "20%", // Makes it a perfect circle
                          bgcolor: "rgba(255, 255, 255, 0.1)",
                          "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
                        }}
                        onClick={() => downloadPdf(slide.document)}
                      >
                        <i
                          className="fa-solid fa-file-pdf"
                          style={{ color: "white" }}
                        ></i>
                      </IconButton>
                    ) : null}
                    <IconButton
                      sx={{
                        mr: 1,
                        width: 40, // Equal width
                        height: 40, // Equal height
                        borderRadius: "50%", // Makes it a perfect circle
                        bgcolor: "rgba(255, 255, 255, 0.1)",
                        "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
                      }}
                      onClick={() => window.open(slide.whatsapp_link, "_blank")}
                    >
                      <i
                        className="fa-brands fa-whatsapp"
                        style={{ color: "white" }}
                      ></i>
                    </IconButton>
                    <IconButton
                      sx={{
                        width: 40, // Equal width
                        height: 40, // Equal height
                        borderRadius: "50%", // Makes it a perfect circle
                        bgcolor: "rgba(255, 255, 255, 0.1)",
                        "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
                      }}
                      onClick={() => window.open(slide.youtube_link, "_blank")}
                    >
                      <i
                        className="fa-brands fa-youtube"
                        style={{ color: "white" }}
                      ></i>
                    </IconButton>
                  </Box>
                </Box>
                <Box
                  sx={{
                    bgcolor: "rgba(255,255,255,0.1)",
                    borderRadius: "16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    my: 1,
                    fontWeight: "bold",
                    width: "100%",
                  }}
                >
                  <Accordion sx={{ borderRadius: "16px", width: "100%" }}>
                    <AccordionSummary
                      expandIcon={
                        <ArrowDownwardRoundedIcon sx={{ color: "#023047" }} />
                      }
                      aria-controls="panel1-content"
                      id="panel1-header"
                    >
                      <Typography variant="body1" fontWeight="bold">
                        <i
                          className="fa-solid fa-user-group"
                          style={{ marginRight: 10 }}
                        ></i>
                        Mentors
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                        width: "100%",
                      }}
                    >
                      {slide.mentors.map((mentors, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                          }}
                        >
                          <ArrowRightRoundedIcon />
                          <Typography fontWeight="bold">
                            {mentors.name}
                          </Typography>
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                </Box>
                <Typography sx={{ my: 1 }}>{slide.description}</Typography>
              </Box>
            </Box>
          </Carousel.Item>
        ))}
      </Carousel>
    </Box>
  );
}
